export const clearBulkUploadSubscription = (consumer, subscriptionRef) => {
    const subscription = subscriptionRef.current

    if(consumer?.subscriptions && subscription) {
        consumer.subscriptions.remove(subscription)
    }

    subscriptionRef.current = null
}

export const createBulkUploadLifecycle = ({
    clearIntervalFn = clearInterval,
    clearTimeoutFn = clearTimeout,
} = {}) => {
    const uploads = new Map()
    let disposed = false

    const registeredUpload = id => {
        if(disposed) return null

        return uploads.get(id)
    }

    const registerUpload = id => {
        if(disposed) return null

        if(!uploads.has(id)) {
            uploads.set(id, {
                phase: 'preparing',
                intervalId: null,
                statusRequestInFlight: false,
                retryTimeouts: new Set(),
                closeMessage: null,
            })
        }

        return uploads.get(id)
    }

    const clearPollInterval = upload => {
        if(upload.intervalId === null) return

        clearIntervalFn(upload.intervalId)
        upload.intervalId = null
    }

    const clearRetryTimeouts = upload => {
        upload.retryTimeouts.forEach(timeoutId => clearTimeoutFn(timeoutId))
        upload.retryTimeouts.clear()
    }

    const closeMessage = upload => {
        if(!upload.closeMessage) return

        upload.closeMessage()
        upload.closeMessage = null
    }

    return {
        start(id, {intervalId = null, closeMessage: messageCloser = null, pollNow = null} = {}) {
            const upload = registerUpload(id)

            if(!upload) {
                if(intervalId !== null) clearIntervalFn(intervalId)
                if(messageCloser) messageCloser()
                return false
            }

            if(intervalId !== null) {
                clearPollInterval(upload)

                if(upload.phase === 'preparing') upload.intervalId = intervalId
                else clearIntervalFn(intervalId)
            }

            if(messageCloser) {
                upload.closeMessage = messageCloser
                if(upload.phase === 'error' || upload.phase === 'complete') closeMessage(upload)
            }

            if(upload.phase !== 'preparing') return false

            if(pollNow) pollNow()
            return true
        },

        setCloseMessage(id, messageCloser) {
            const upload = registeredUpload(id)

            if(!upload) {
                messageCloser()
                return
            }

            upload.closeMessage = messageCloser
            if(upload.phase === 'error' || upload.phase === 'complete') closeMessage(upload)
        },

        beginStatusRequest(id) {
            const upload = registeredUpload(id)

            if(!upload || upload.phase !== 'preparing' || upload.statusRequestInFlight) return false

            upload.statusRequestInFlight = true
            return true
        },

        finishStatusRequest(id) {
            const upload = uploads.get(id)
            if(upload) upload.statusRequestInFlight = false
        },

        claimReady(id) {
            const upload = registeredUpload(id)

            if(!upload || upload.phase !== 'preparing') return false

            upload.phase = 'ready'
            upload.statusRequestInFlight = false
            clearPollInterval(upload)
            return true
        },

        claimError(id) {
            const upload = registeredUpload(id)

            if(!upload || upload.phase !== 'preparing') return false

            upload.phase = 'error'
            upload.statusRequestInFlight = false
            clearPollInterval(upload)
            clearRetryTimeouts(upload)
            closeMessage(upload)
            return true
        },

        trackRetryTimeout(id, timeoutId) {
            const upload = uploads.get(id)

            if(disposed || !upload || upload.phase !== 'ready') {
                clearTimeoutFn(timeoutId)
                return false
            }

            upload.retryTimeouts.add(timeoutId)
            return true
        },

        releaseRetryTimeout(id, timeoutId) {
            uploads.get(id)?.retryTimeouts.delete(timeoutId)
        },

        isReady(id) {
            return !disposed && uploads.get(id)?.phase === 'ready'
        },

        isPreparing(id) {
            return !disposed && uploads.get(id)?.phase === 'preparing'
        },

        completeReady(id) {
            const upload = uploads.get(id)
            if(!upload || upload.phase !== 'ready') return false

            upload.phase = 'complete'
            clearRetryTimeouts(upload)
            closeMessage(upload)
            return true
        },

        cancel(id) {
            const upload = uploads.get(id)
            if(!upload) return false

            upload.phase = 'cancelled'
            upload.statusRequestInFlight = false
            clearPollInterval(upload)
            clearRetryTimeouts(upload)
            closeMessage(upload)
            return true
        },

        dispose() {
            if(disposed) return

            disposed = true
            uploads.forEach(upload => {
                clearPollInterval(upload)
                clearRetryTimeouts(upload)
                closeMessage(upload)
                upload.statusRequestInFlight = false
            })
        },
    }
}
